import React, { useState } from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { Employee } from '../../../types';
import Input from '../../Form/Input';
import Select from '../../Form/Select';
import { API_CONFIG } from '../../../config/api.config';

export interface ExistingEmployeeDocument {
  documentId: string;
  documentType: string;
  documentNumber?: string | null;
  issueDate?: string | null;
  expiryDate?: string | null;
  filePath?: string | null;
}

interface EmployeePersonalInfoProps {
  register: UseFormRegister<Employee>;
  errors: FieldErrors<Employee>;
  documentFile: File | null;
  setDocumentFile: (file: File | null) => void;
  documentType: string;
  setDocumentType: (value: string) => void;
  documentIssueDate: string;
  setDocumentIssueDate: (value: string) => void;
  documentExpiryDate: string;
  setDocumentExpiryDate: (value: string) => void;
  nationalityOptions: { value: string; label: string }[];
  documentTypeOptions: { value: string; label: string }[];
  existingDocuments: ExistingEmployeeDocument[];
  onUpdateDocument: (
    doc: ExistingEmployeeDocument,
    changes: { documentType: string; documentNumber: string; issueDate: string; expiryDate: string; newFile: File | null }
  ) => Promise<void>;
  onDeleteDocument: (documentId: string) => Promise<void>;
}

const fileUrl = (path: string): string =>
  path.startsWith('http') ? path : `${API_CONFIG.BASE_URL}${path}`;

const EmployeePersonalInfo: React.FC<EmployeePersonalInfoProps> = ({
  register,
  errors,
  documentFile,
  setDocumentFile,
  documentType,
  setDocumentType,
  documentIssueDate,
  setDocumentIssueDate,
  documentExpiryDate,
  setDocumentExpiryDate,
  nationalityOptions,
  documentTypeOptions,
  existingDocuments,
  onUpdateDocument,
  onDeleteDocument
}) => {
  const nationalIdFileName = documentFile?.name || '';
  const [editingDocId, setEditingDocId] = useState<string>('');
  const [editType, setEditType] = useState<string>('');
  const [editNumber, setEditNumber] = useState<string>('');
  const [editIssueDate, setEditIssueDate] = useState<string>('');
  const [editExpiryDate, setEditExpiryDate] = useState<string>('');
  const [editFile, setEditFile] = useState<File | null>(null);
  const [docBusy, setDocBusy] = useState(false);
  const [docError, setDocError] = useState<string>('');

  const startEditing = (doc: ExistingEmployeeDocument) => {
    setEditingDocId(doc.documentId);
    setEditType(doc.documentType || '');
    setEditNumber(doc.documentNumber || '');
    setEditIssueDate(doc.issueDate || '');
    setEditExpiryDate(doc.expiryDate || '');
    setEditFile(null);
    setDocError('');
  };

  const stopEditing = () => {
    setEditingDocId('');
    setEditFile(null);
    setDocError('');
  };

  const saveEditing = async (doc: ExistingEmployeeDocument) => {
    setDocBusy(true);
    setDocError('');
    try {
      await onUpdateDocument(doc, { documentType: editType, documentNumber: editNumber, issueDate: editIssueDate, expiryDate: editExpiryDate, newFile: editFile });
      stopEditing();
    } catch (e: any) {
      setDocError(e?.message || 'Failed to update document');
    } finally {
      setDocBusy(false);
    }
  };

  const deleteDocument = async (doc: ExistingEmployeeDocument) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المستند؟')) return;
    setDocBusy(true);
    setDocError('');
    try {
      await onDeleteDocument(doc.documentId);
    } catch (e: any) {
      setDocError(e?.message || 'Failed to delete document');
    } finally {
      setDocBusy(false);
    }
  };

  // Stored document types are canonical names; keep the current value
  // selectable even if it isn't one of the known options.
  const editTypeChoices = editType && !documentTypeOptions.some(o => o.value === editType)
    ? [{ value: editType, label: editType }, ...documentTypeOptions]
    : documentTypeOptions;
  return (
    <>
      <div className="col-span-full">
        <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2 mt-6">
          Personal Information
        </h3>
      </div>
      
      <Input
        label="National ID (الرقم القومي)"
        {...register('nationalId')}
        error={errors.nationalId?.message}
        placeholder="123456789"
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Document Type (نوع الوثيقة)
        </label>
        <select
          value={documentType}
          onChange={(e) => setDocumentType(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">Select Document Type</option>
          {documentTypeOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Document Issue Date (تاريخ الإصدار)
        </label>
        <input
          type="date"
          value={documentIssueDate}
          onChange={(e) => setDocumentIssueDate(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Document Expiry Date (تاريخ انتهاء الوثيقة)
        </label>
        <input
          type="date"
          value={documentExpiryDate}
          onChange={(e) => setDocumentExpiryDate(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      <div className="col-span-full">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Document File (صورة الوثيقة)
        </label>
        <div className="flex items-center justify-center w-full">
          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              {nationalIdFileName ? (
                <>
                  <svg className="w-8 h-8 mb-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="mb-1 text-sm text-gray-700 font-medium">{nationalIdFileName}</p>
                  <p className="text-xs text-gray-500">Click to change file</p>
                </>
              ) : (
                <>
                  <svg className="w-8 h-8 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="mb-1 text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> document image
                  </p>
                  <p className="text-xs text-gray-500">PDF, PNG, JPG or JPEG (Max 10MB)</p>
                </>
              )}
            </div>
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              className="hidden"
              onChange={(e) => {
                setDocumentFile(e.target.files?.[0] || null);
              }}
            />
          </label>
        </div>
      </div>

      {existingDocuments.length > 0 && (
        <div className="col-span-full">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Uploaded Documents (المستندات المرفوعة)
          </label>
          <div className="space-y-2">
            {existingDocuments.map((doc) => (
              <div key={doc.documentId} className="bg-gray-50 border border-gray-200 rounded-md px-4 py-2 text-sm">
                {editingDocId === doc.documentId ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <select
                        value={editType}
                        onChange={(e) => setEditType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        {editTypeChoices.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={editNumber}
                        onChange={(e) => setEditNumber(e.target.value)}
                        placeholder="Document Number (رقم الوثيقة)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <input
                        type="date"
                        value={editIssueDate}
                        onChange={(e) => setEditIssueDate(e.target.value)}
                        title="Issue Date (تاريخ الإصدار)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <input
                        type="date"
                        value={editExpiryDate}
                        onChange={(e) => setEditExpiryDate(e.target.value)}
                        title="Expiry Date (تاريخ الانتهاء)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="text-ocean-600 hover:text-ocean-800 underline cursor-pointer">
                        {editFile ? editFile.name : 'استبدال الملف'}
                        <input
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg"
                          className="hidden"
                          onChange={(e) => setEditFile(e.target.files?.[0] || null)}
                        />
                      </label>
                      {doc.filePath && !editFile && (
                        <a href={fileUrl(doc.filePath)} target="_blank" rel="noreferrer" className="text-gray-500 underline">
                          الملف الحالي
                        </a>
                      )}
                      <span className="flex-1" />
                      <button
                        type="button"
                        disabled={docBusy}
                        onClick={() => saveEditing(doc)}
                        className="px-3 py-1 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                      >
                        {docBusy ? '...' : 'حفظ'}
                      </button>
                      <button
                        type="button"
                        disabled={docBusy}
                        onClick={stopEditing}
                        className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                      >
                        إلغاء
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-gray-900">
                      {doc.documentType}
                      {doc.documentNumber ? ` — ${doc.documentNumber}` : ''}
                      {doc.expiryDate ? ` · تنتهي ${new Date(doc.expiryDate).toLocaleDateString('ar-EG')}` : ''}
                    </span>
                    <span className="flex items-center gap-3">
                      {doc.filePath && (
                        <a
                          href={fileUrl(doc.filePath)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-ocean-600 hover:text-ocean-800 underline"
                        >
                          عرض الملف
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => startEditing(doc)}
                        className="text-primary-600 hover:text-primary-800 underline"
                      >
                        تعديل
                      </button>
                      <button
                        type="button"
                        disabled={docBusy}
                        onClick={() => deleteDocument(doc)}
                        className="text-red-600 hover:text-red-800 underline disabled:opacity-50"
                      >
                        حذف
                      </button>
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
          {docError && <p className="mt-2 text-sm text-red-600">{docError}</p>}
        </div>
      )}
      
      <Input
        label="ID Card Expiry Date (تاريخ انتهاء البطاقة)"
        type="date"
        {...register('cardExpiryDate')}
        error={errors.cardExpiryDate?.message}
      />
      
      <Input
        label="Birth Date (تاريخ الميلاد)"
        type="date"
        {...register('birthDate')}
        error={errors.birthDate?.message}
      />
      
      <Select
        label="Nationality (الجنسية)"
        {...register('nationality')}
        options={nationalityOptions}
        error={errors.nationality?.message}
      />
      
      <Input
        label="Address (العنوان)"
        {...register('address')}
        error={errors.address?.message}
        placeholder="Full address"
        className="md:col-span-2"
      />
    </>
  );
};

export default EmployeePersonalInfo;
